/**
 * Headlight Platform Configuration — Master List builder.
 *
 * Turns the data-driven master-list catalog (Headlight-MasterLists.js, or any array of
 * the same shape loaded later from a ConfigFragment) into the two artifacts the app
 * needs: pict-section-recordset configurations and a navigation section.
 *
 * Lists run "manifest-less": with no RecordSetListManifestOnly flag and no manifest,
 * pict-section-recordset pulls the column set and the create / edit / view forms from
 * the entity's /1.0/<Entity>/Schema endpoint. A curated Columns array (optional, per
 * entry) overrides the auto-generated columns.
 */

const DEFAULT_URL_PREFIX = '/1.0/';

/**
 * Is this a usable catalog entry (has a non-empty Entity name)?
 * @param {Object} pEntry - a master-list catalog entry.
 * @return {Boolean}
 */
function isValidEntry(pEntry)
{
	return !!(pEntry && typeof pEntry.Entity === 'string' && pEntry.Entity.length > 0);
}

/**
 * Does this column key name another record's ID (IDMaterial, CreatingIDUser, SampleDetails.IDProject)? The record's own
 * primary key (IDSample on Sample) does not count.
 * @param {String} pKey - a list column's Key.
 * @param {String} pEntity - the list's entity.
 * @return {Boolean}
 */
function isForeignKeyColumn(pKey, pEntity)
{
	const tmpField = String(pKey || '').split('.').pop();
	return /^([A-Z][a-z]+)?ID[A-Z]/.test(tmpField) && (tmpField !== `ID${ pEntity }`);
}

/**
 * The list columns for a sortable entry, with every foreign-key column left plain: it shows the linked record's name
 * but would sort by the hidden ID. A column that sets Sortable itself keeps it. Other entries' columns pass through.
 * @param {Array<Object|String>} pColumns - an entry's Columns (objects, or bare keys).
 * @param {Object} pEntry - the master-list catalog entry.
 * @return {Array<Object|String>} a new array; columns that change are copies.
 */
function markForeignKeyColumnsUnsortable(pColumns, pEntry)
{
	if (!pEntry || (pEntry.Sortable !== true))
	{
		return pColumns.slice();
	}
	return pColumns.map((pColumn) =>
	{
		const tmpKey = (typeof pColumn === 'string') ? pColumn : (pColumn && pColumn.Key);
		if (!isForeignKeyColumn(tmpKey, pEntry.Entity) || ((typeof pColumn === 'object') && (typeof pColumn.Sortable === 'boolean')))
		{
			return pColumn;
		}
		const tmpColumn = (typeof pColumn === 'string') ? { Key: pColumn, DisplayName: pColumn } : Object.assign({}, pColumn);
		tmpColumn.Sortable = false;
		return tmpColumn;
	});
}

/**
 * The registered pict-view identifier for a (parent, child) FK child-list tab. The builder points a
 * { Type: 'View' } read tab at it; PlatformConfiguration-Application-Web.js registers one ChildListTab
 * instance under the same id. Keyed by FilterField too, so a parent with two FK children on the same
 * entity (different foreign keys) gets distinct instances.
 * @param {String} pParentEntity - the entity whose read view hosts the tab.
 * @param {Object} pChildSpec - a ChildLists entry { RecordSet, FilterField, ... }.
 * @return {String}
 */
function childListViewId(pParentEntity, pChildSpec)
{
	return `RSP-ChildList-${ pParentEntity }-${ pChildSpec.RecordSet }-${ pChildSpec.FilterField }`;
}

/**
 * Build an entry's combined read-tab list: an Association editor tab per join (ReadTabs) followed by a
 * filtered child-list tab per FK child (ChildLists → a { Type: 'View' } tab at a ChildListTab instance).
 * @param {Object} pEntry - a master-list catalog entry.
 * @return {Array} RecordSetReadTabs entries.
 */
function buildReadTabs(pEntry)
{
	const tmpTabs = [];
	(Array.isArray(pEntry.ReadTabs) ? pEntry.ReadTabs : []).forEach((pTab) =>
	{
		tmpTabs.push({ Type: 'Association', Association: pTab.Association, Title: pTab.Title });
	});
	(Array.isArray(pEntry.ChildLists) ? pEntry.ChildLists : []).forEach((pChild) =>
	{
		tmpTabs.push({ Type: 'View', View: childListViewId(pEntry.Entity, pChild), Title: pChild.Title });
	});
	// Bespoke view tabs — a { View, Title } pointing at a hand-registered view (e.g. SpecSetMaterials).
	(Array.isArray(pEntry.ViewTabs) ? pEntry.ViewTabs : []).forEach((pViewTab) =>
	{
		if (pViewTab && pViewTab.View) { tmpTabs.push({ Type: 'View', View: pViewTab.View, Title: pViewTab.Title || pViewTab.View }); }
	});
	return tmpTabs;
}

/**
 * Build a pict-section-recordset configuration for a single master-list entry.
 * @param {Object} pEntry - a master-list catalog entry (see Headlight-MasterLists.js).
 * @return {Object} a recordset configuration for loadRecordSetConfigurationArray.
 */
function buildRecordSetConfiguration(pEntry)
{
	const tmpConfiguration =
	{
		'RecordSet': pEntry.Entity,

		'RecordSetType': 'MeadowEndpoint',
		'RecordSetMeadowEntity': pEntry.Entity,

		'RecordSetURLPrefix': pEntry.URLPrefix || DEFAULT_URL_PREFIX,
		'Title': pEntry.Label || pEntry.Entity,
		'SearchFields': Array.isArray(pEntry.SearchFields) ? pEntry.SearchFields : [ 'Name' ]
	};

	// pict-section-recordset applies RecordSetDefaultFilter only when the list route names no /FilteredTo/ of its own.
	if ((typeof pEntry.DefaultFilter === 'string') && (pEntry.DefaultFilter.length > 0))
	{
		tmpConfiguration.RecordSetDefaultFilter = pEntry.DefaultFilter;
	}

	// Clickable column headers; the sort rides the list route's FSF stanza, so it runs server-side across every page.
	if (pEntry.Sortable === true)
	{
		tmpConfiguration.RecordSetListSortable = true;
	}

	// Quick filters are opt-in in this app (ComplexFilterProvider), so a list shows the bar only when its entry names them.
	if (Array.isArray(pEntry.QuickFilters) && (pEntry.QuickFilters.length > 0))
	{
		tmpConfiguration.QuickFilters = pEntry.QuickFilters.slice();
	}

	// A curated column list is optional — omitting it lets pict-section-recordset
	// generate columns from the Meadow schema (the "auto for everything" default).
	if (Array.isArray(pEntry.Columns) && (pEntry.Columns.length > 0))
	{
		tmpConfiguration.RecordSetListColumns = markForeignKeyColumnsUnsortable(pEntry.Columns, pEntry);
	}

	// Optional advanced-filter overrides, passed straight through to the recordset provider
	// (RecordSet-RecordProvider-MeadowEndpoints reads both off its options):
	//   Filters            — per-field customization MERGED onto an auto-generated clause; for a
	//                        foreign key this is the picker, so widening ExternalFilterByColumns lets
	//                        the picker be found by more than Name (e.g. a material's code columns).
	//   FieldFilterClauses — a per-field REPLACEMENT clause array; when present the module skips its
	//                        auto-generated clauses for that field entirely (e.g. swap a free-entry
	//                        Exact/Partial/Range set for a single pick-list clause).
	if (pEntry.Filters && (typeof pEntry.Filters === 'object'))
	{
		tmpConfiguration.Filters = pEntry.Filters;
	}
	if (pEntry.FieldFilterClauses && (typeof pEntry.FieldFilterClauses === 'object'))
	{
		tmpConfiguration.FieldFilterClauses = pEntry.FieldFilterClauses;
	}

	// Read tabs — an Association editor per join (ReadTabs) + a filtered child list per FK child
	// (ChildLists). ReadLayout defaults to 'Split': the record is the page, its connection tabs sit as a
	// right-justified strip on top; opening one reveals its editor beside the record (the app hides the
	// module's "Full Record" collapse toggle so only connection tabs show). Override with 'Tab' or 'Basic'.
	const tmpReadTabs = buildReadTabs(pEntry);
	if (tmpReadTabs.length > 0)
	{
		tmpConfiguration.ReadLayout = pEntry.ReadLayout || 'Split';
		tmpConfiguration.RecordSetReadTabs = tmpReadTabs;
	}
	else if (pEntry.ReadLayout)
	{
		tmpConfiguration.ReadLayout = pEntry.ReadLayout;
	}

	// Bulk association screens — a dedicated "Assign …" screen per listed association, reached at
	// #/PSRS/<Entity>/Associate/<Association>. Use this (instead of, or alongside, ReadTabs) when an
	// association is better managed in bulk than inline on each record. The builder also emits a
	// navigation entry for each (see buildCategorizedSections).
	if (Array.isArray(pEntry.BulkAssociations) && (pEntry.BulkAssociations.length > 0))
	{
		tmpConfiguration.RecordSetBulkAssociations = pEntry.BulkAssociations.map((pBulk) =>
			({ Association: pBulk.Association, Title: pBulk.Title }));
	}

	return tmpConfiguration;
}

/**
 * Build the recordset configuration array for a catalog of master-list entries.
 * @param {Array} pCatalog - array of master-list catalog entries.
 * @return {Array} recordset configurations.
 */
function buildRecordSetConfigurations(pCatalog)
{
	if (!Array.isArray(pCatalog))
	{
		return [];
	}
	return pCatalog.filter(isValidEntry).map(buildRecordSetConfiguration);
}

/**
 * Apply an entry's association-view choices to an already-built / live recordset configuration.
 *
 * The read-tab and bulk-screen surfaces are now cascading config (not code), but the live
 * recordset configs are built from the static seed at construction time — so when the merged
 * config arrives (or a save re-applies), these fields must be pushed onto the live config. This
 * sets RecordSetReadTabs + ReadLayout + RecordSetBulkAssociations, clearing them when the
 * corresponding array is empty (an emptied view list is a real "show none" intent — arrays
 * replace wholesale). Matrix / unlink screens are navigation-only (no recordset field) and are
 * handled by rebuilding the nav.
 * @param {Object} pLiveConfig - the live recordset configuration to mutate.
 * @param {Object} pEntry - a master-list catalog entry.
 * @return {Object} the mutated live configuration.
 */
function applyAssociationViewsToConfig(pLiveConfig, pEntry)
{
	if (!pLiveConfig || !pEntry)
	{
		return pLiveConfig;
	}

	const tmpReadTabs = buildReadTabs(pEntry);
	pLiveConfig.RecordSetReadTabs = tmpReadTabs;
	pLiveConfig.ReadLayout = pEntry.ReadLayout || ((tmpReadTabs.length > 0) ? 'Split' : 'Basic');

	const tmpBulk = Array.isArray(pEntry.BulkAssociations) ? pEntry.BulkAssociations : [];
	pLiveConfig.RecordSetBulkAssociations = tmpBulk.map((pBulk) =>
		({ Association: pBulk.Association, Title: pBulk.Title }));

	return pLiveConfig;
}

/**
 * Build a navigation child node for a single master-list entry.
 * @param {Object} pEntry - a master-list catalog entry.
 * @return {Object} a pict-section-navigation child node.
 */
function buildNavigationChild(pEntry)
{
	return (
	{
		Name: pEntry.Label || pEntry.Entity,
		Icon: pEntry.Icon || 'fas fa-list',
		Route: `#/PSRS/${pEntry.Entity}/List`,
		Keywords: pEntry.Keywords || '',
		Description: pEntry.Description || ''
	});
}

/**
 * Build the "Reference Data" navigation section holding every master list. A single,
 * non-disruptive section for now; the per-domain retool (using each entry's Domain)
 * is a deliberate later step.
 * @param {Array} pCatalog - array of master-list catalog entries.
 * @return {Object} a pict-section-navigation section node.
 */
function buildNavigationSection(pCatalog)
{
	const tmpChildren = (Array.isArray(pCatalog) ? pCatalog : []).filter(isValidEntry).map(buildNavigationChild);

	return (
	{
		Name: 'Reference Data',
		Icon: 'fas fa-database',
		Accent: 'var(--theme-color-brand-primary)',
		Blurb: 'Catalog and lookup lists the platform draws from.',
		Children:
		[
			{
				Name: 'Manage Lists',
				Icon: 'fas fa-sliders',
				Route: '#/MasterLists',
				Keywords: 'configure columns search master list settings curate',
				Description: 'Choose which columns and search fields each reference list shows.'
			}
		].concat(tmpChildren)
	});
}

/**
 * Build the Master Lists navigation section: a "Manage Lists" entry followed by every
 * list as its own discrete, directly-navigable entry (flat, alphabetical — like the home
 * app). The category is an organizing attribute used by the Manage Lists tool's rail, not
 * a navigation node, so the nav has no non-clickable "combined" group links. Marked
 * MasterListCategory so the registry can swap it at runtime. (`pCategories` is unused now
 * but kept for call-site compatibility.)
 * @param {Array} pEntries - master-list catalog entries (enabled).
 * @param {Array} [pCategories] - category metadata (unused; flat nav).
 * @return {Array} a one-section array for the navigation graph.
 */
function buildCategorizedSections(pEntries, pCategories)
{
	const tmpValidEntries = (Array.isArray(pEntries) ? pEntries : []).filter(isValidEntry);

	const tmpListChildren = tmpValidEntries
		.slice()
		.sort((pA, pB) => (pA.Label || pA.Entity).localeCompare(pB.Label || pB.Entity))
		.map(buildNavigationChild);

	// A flat nav entry for each opted-in standalone association SCREEN. A list opts a connection
	// into a screen by listing it under one of these fields; the route differs per screen type
	// (bulk is recordset-centric; matrix / unlink are association-centric, anchored to this list).
	const tmpScreenSpecifications =
	[
		{ Field: 'BulkAssociations',   Icon: 'fas fa-arrow-right-arrow-left', route: (pEntity, pAssociation) => `#/PSRS/${pEntity}/Associate/${pAssociation}` },
		{ Field: 'MatrixAssociations', Icon: 'fas fa-table-cells',            route: (pEntity, pAssociation) => `#/PSRS/AssociateMatrix/${pAssociation}/${pEntity}` },
		{ Field: 'UnlinkAssociations', Icon: 'fas fa-link-slash',             route: (pEntity, pAssociation) => `#/PSRS/AssociateUnlink/${pAssociation}/${pEntity}` }
	];
	const tmpScreenChildren = [];
	tmpValidEntries.forEach((pEntry) =>
	{
		tmpScreenSpecifications.forEach((pSpecification) =>
		{
			(Array.isArray(pEntry[pSpecification.Field]) ? pEntry[pSpecification.Field] : []).forEach((pScreen) =>
			{
				tmpScreenChildren.push(
				{
					Name: pScreen.Title || `${pEntry.Label || pEntry.Entity} associations`,
					Icon: pScreen.Icon || pSpecification.Icon,
					Route: pSpecification.route(pEntry.Entity, pScreen.Association),
					Keywords: `associate ${pEntry.Entity} ${pScreen.Association}`,
					Description: pScreen.Description || `Manage associations for ${pEntry.Label || pEntry.Entity}.`
				});
			});
		});
	});

	const tmpManageEntry =
	{
		Name: 'Manage Lists',
		Icon: 'fas fa-sliders',
		Route: '#/MasterLists',
		Keywords: 'configure columns search master list settings curate add remove category',
		Description: 'Add, remove, categorize and curate the reference lists.'
	};

	return (
	[
		{
			Name: 'Master Lists',
			Icon: 'fas fa-database',
			Accent: 'var(--theme-color-brand-primary)',
			Blurb: 'Catalog and lookup lists the platform draws from.',
			MasterListCategory: true,
			Children: [ tmpManageEntry ].concat(tmpListChildren).concat(tmpScreenChildren)
		}
	]);
}

module.exports =
{
	buildRecordSetConfiguration,
	buildRecordSetConfigurations,
	applyAssociationViewsToConfig,
	buildNavigationChild,
	buildNavigationSection,
	buildCategorizedSections,
	childListViewId,
	buildReadTabs,
	markForeignKeyColumnsUnsortable
};
